import { createEmptySurfaceStore, KnownItemTypes } from 'owa-calendar-surface-dategrid';
import { createLockedCalendarEventsStore } from 'owa-calendar-events-store';
import { DateRangeType } from '@fluentui/date-time-utilities/lib/dateValues/dateValues';
import { now } from 'owa-datetime';
import { mutator } from 'satcheljs';
import getFullDayFromDate from '../utils/getFullDayFromDate';
import getOwaDayGridOptions from '../utils/getOwaDayGridOptions';
import {
    updateFolderIds,
    resetDayGrid,
    updateNumDaysInDayRange,
    updateDateRangeType,
    getScenarioStore,
    getStore,
} from 'owa-day-grid-core-schema';
import { initializeDayGrid } from '../actions/publicActions';
import {
    selectedDateChanged,
    selectedItemChanged,
    updateUserOptions,
} from '../actions/privateActions';
import { getDateRangeLength } from 'owa-datetime-utils';
import { createTimeZoneArray } from 'owa-datetime-utils/lib/createTimeZoneArray';
import { convertToSchedulingSelectionItem } from 'owa-day-grid-core-utilities';

mutator(
    initializeDayGrid,
    ({
        scenarioId,
        folderIds,
        selectedDate,
        selectionItem,
        showSelectionItem,
        disableTabbableSurfaceItems,
        daysInDayRange,
        enableDragDrop,
        dateRangeType,
    }) => {
        const storeValue = {
            ...createEmptySurfaceStore(DateRangeType.Day),
            dateRange: getFullDayFromDate(selectedDate || now(), daysInDayRange || 1),
            userOptions: getOwaDayGridOptions(),
            disableTabbableSurfaceItems: disableTabbableSurfaceItems,
            enableDragDrop: enableDragDrop,
            timeZoneIds: selectionItem
                ? createTimeZoneArray(selectionItem.Start.tz, selectionItem.End.tz)
                : [(selectedDate || now()).tz],
            selectedDate: selectionItem ? selectionItem.Start : selectedDate,
            selectionItem: selectionItem
                ? convertToSchedulingSelectionItem(selectionItem, KnownItemTypes.suggestedItems)
                : undefined,
            showSelectionItem: showSelectionItem,
            eventsCacheLockId: createLockedCalendarEventsStore().getLockId(),
            folderIds: folderIds,
            dateRangeType: dateRangeType,
        };

        getStore().scenarios.set(scenarioId, storeValue);
    }
);

mutator(updateUserOptions, ({ scenarioId, userOptions }) => {
    const store = getScenarioStore(scenarioId);
    store.userOptions = userOptions;
});

mutator(updateNumDaysInDayRange, ({ scenarioId, numDaysInDayRange }) => {
    const store = getScenarioStore(scenarioId);
    store.dateRange = getFullDayFromDate(store.selectedDate || now(), numDaysInDayRange || 1);
});

mutator(updateFolderIds, ({ scenarioId, folderIds }) => {
    const store = getScenarioStore(scenarioId);
    store.folderIds = folderIds;
});

mutator(selectedDateChanged, ({ scenarioId, newDate }) => {
    const store = getScenarioStore(scenarioId);
    store.selectedDate = newDate;
    store.dateRange = getFullDayFromDate(newDate, getDateRangeLength(store.dateRange) + 1);
});

mutator(selectedItemChanged, ({ scenarioId, selectionItem }) => {
    const store = getScenarioStore(scenarioId);
    const newTimeZoneArray = createTimeZoneArray(selectionItem.Start.tz, selectionItem.End.tz);

    const needsUpdate = newTimeZoneArray.some((value: string, index: number) => {
        return store.timeZoneIds[index] != value;
    });

    // if the arrays are the same, don't overwrite because mobx will fire unnecessary rerenders
    if (needsUpdate) {
        store.timeZoneIds = newTimeZoneArray;
    }

    store.selectionItem = convertToSchedulingSelectionItem(
        selectionItem,
        KnownItemTypes.suggestedItems
    );
});

mutator(resetDayGrid, ({ scenarioId }) => {
    getStore().scenarios.set(scenarioId, null);
});

mutator(updateDateRangeType, ({ scenarioId, dateRangeType }) => {
    const store = getScenarioStore(scenarioId);
    store.dateRangeType = dateRangeType;
});
